import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

const api = clientFor('/ops/api')

/** 计费明细状态：pending → settled（结算付款）；pending → void（作废） */
export type ChargeStatus = 'pending' | 'settled' | 'void'

/** 计费明细：订单签收后按计费规则算出的一笔应收。字段与 internal/charge/model.go 一致。 */
export interface Charge {
  id: number
  tenant_id: number
  charge_no: string
  order_id: number
  rate_card_id: number
  qty: number
  amount: number
  status: ChargeStatus
  settle_id: number
  created_at: string
  updated_at: string
}

/** 列表行：明细 + 订单号 + 客户 + 计费规则名（后端用相关子查询补齐，不 JOIN） */
export interface ChargeRow extends Charge {
  order_no: string
  customer_id: number
  customer_name: string
  rate_card_name: string
}

export interface ChargeQuery extends PageQuery {
  keyword?: string
  customer?: number
  order_id?: number
  status?: string
}

/** 与后端 services/ops/internal/charge/model.go 的 StatusNames 同源两份，改一边必须改另一边 */
export const STATUS_LABELS: Record<ChargeStatus, string> = {
  pending: '待结算',
  settled: '已结算',
  void: '已作废',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<ChargeStatus, 'warning' | 'success' | 'info'> = {
  pending: 'warning',
  settled: 'success',
  void: 'info',
}

export const listCharges = (q: ChargeQuery): Promise<PageResult<ChargeRow>> =>
  api.get<PageResult<ChargeRow>>('/charges', { ...q })

export const getCharge = (id: number): Promise<ChargeRow> => api.get<ChargeRow>(`/charges/${id}`)

/** 作废一条待结算计费；已挂结算单或已结算的由后端 409 挡住 */
export const voidCharge = (id: number): Promise<void> =>
  api.post<void>(`/charges/${id}/void`, {})

/**
 * 导出 CSV：同一个路径带 export=csv，后端回 UTF-8 BOM 附件。
 * 导出走的是 ops:report:export（不是结算/订单的读权限），由页面按钮的 v-perm 控制入口。
 */
export const exportCharges = (q: Omit<ChargeQuery, 'page' | 'size'>): Promise<Blob> =>
  api.getBlob('/charges', { ...q, export: 'csv' })
